// Type definitions for archive datasheet
import { ContentType, StrapiMedia, Resources } from "../types/strapi";
import countries from "i18n-iso-countries";
import enLocale from "i18n-iso-countries/langs/en.json";
import esLocale from "i18n-iso-countries/langs/es.json";
import i18n from "../i18n";

countries.registerLocale(enLocale);
countries.registerLocale(esLocale);

interface DatasheetRelation {
  name?: string;
}

interface KeywordItem {
  name: string;
}

export interface Datasheet {
  id: number;
  title?: string;
  description?: string;
  annotation?: string;
  author?: string;
  subject?: string;
  publishedAt?: string;
  keywords?: KeywordItem[];
  location?: string;
  Interviewee?: string;
  collaborators?: string;
  socialActor?: string;
  dublin_core_type?: DatasheetRelation | null;
  category?: DatasheetRelation | null;
  doc_type?: DatasheetRelation | null;
  language?: DatasheetRelation | null;
  rights?: string;
  rightsAnnotation?: string;
  useRestriction?: string;
  distributionRestriction?: string;
  publisher?: string;
  publicationDate?: string;
  format?: string;
  coverage?: string;
  numberOfPages?: string;
  sizeDimensions?: string;
  collectionDate?: string;
  source?: string;
  country?: string;
  city?: string;
  file?: StrapiMedia[];
  citation?: string;
  resources?: Resources[];
  url?: string;
  contentType?: ContentType;
  showcase?: {
    name?: string;
  } | null;
  fileType?: string;
  img?: string;
}

interface DetailField {
  heading: string;
  text: string | null;
  colSize?: number;
  resources?: Resources[] | null;
}

const t = (key: string): string => i18n.t(`archiveDetail.${key}`);
const dateLocale = (): string => (i18n.language === "es" ? "es-ES" : "en-US");

export const getArchiveDetailFields = (datasheet: Datasheet): DetailField[] => [
  // 1
  { heading: t("title"), text: datasheet.title ?? null },
  {
    heading:
      datasheet?.description?.length && datasheet.description.length > 120
        ? ""
        : t("description"),
    text: datasheet?.description ?? null,
    colSize: 8,
  },
  { heading: "", text: datasheet.annotation ? " " : null },
  { heading: t("annotation"), text: datasheet.annotation ?? null, colSize: 8 },

  // 2
  {
    heading: t("author"),
    text: datasheet.author ?? null,
    colSize:
      datasheet?.subject?.length && datasheet.subject.length > 0 ? 4 : 12,
  },
  {
    heading: t("subject"),
    text: datasheet?.subject ?? null,
    colSize: 8,
  },

  // 3
  {
    heading: t("publishedOnCem"),
    text: datasheet.publishedAt
      ? new Date(datasheet.publishedAt)?.toLocaleDateString(dateLocale())
      : t("noDate"),
    colSize:
      datasheet?.keywords &&
      Array.isArray(datasheet.keywords) &&
      datasheet.keywords.length > 0
        ? 4
        : 12,
  },
  {
    heading: t("keywords"),
    text: datasheet.keywords
      ? datasheet.keywords.map((item) => item.name).join(", ")
      : null,
    colSize: datasheet?.keywords ? 8 : 0,
  },

  // 4
  { heading: t("location"), text: datasheet.location ?? null },
  { heading: t("interviewee"), text: datasheet.Interviewee ?? null },

  // 4b
  {
    heading: t("country"),
    text: datasheet.country
      ? countries.getName(datasheet.country, i18n.language) ?? datasheet.country
      : null,
  },
  { heading: t("city"), text: datasheet.city ?? null },

  // 5
  {
    heading: t("collaborators"),
    text: datasheet.collaborators ?? null,
  },
  {
    heading: t("socialActors"),
    text: datasheet.socialActor ?? null,
  },

  // 6
  {
    heading: t("dublinCoreType"),
    text: datasheet.dublin_core_type?.name ?? null,
  },
  {
    heading: t("category"),
    text: datasheet.category?.name ?? null,
  },

  // 7
  {
    heading: t("documentType"),
    text: datasheet.doc_type?.name ?? null,
  },
  {
    heading: t("language"),
    text: datasheet.language?.name ?? null,
  },

  // 8
  {
    heading: t("rights"),
    text: datasheet.rights ?? null,
  },
  {
    heading: t("rightsAnnotation"),
    text: datasheet.rightsAnnotation ?? null,
  },

  // 9
  {
    heading: t("useRestriction"),
    text: datasheet.useRestriction ?? null,
  },
  {
    heading: t("distributionRestriction"),
    text: datasheet.distributionRestriction ?? null,
  },

  // 10
  {
    heading: t("publisher"),
    text: datasheet.publisher ?? null,
  },
  {
    heading: t("publicationDate"),
    text: datasheet.publicationDate
      ? new Date(datasheet.publicationDate)?.toLocaleDateString(dateLocale())
      : t("noDate"),
  },

  // 11
  {
    heading: t("format"),
    text: datasheet.format ?? null,
  },
  {
    heading: t("coverage"),
    text: datasheet.coverage ?? null,
  },

  // 12
  {
    heading: t("numberOfPages"),
    text: datasheet.numberOfPages ?? null,
  },
  {
    heading: t("dimensions"),
    text: datasheet.sizeDimensions ?? null,
  },

  // 13
  {
    heading: t("collectionDate"),
    text: datasheet.collectionDate
      ? new Date(datasheet.collectionDate)?.toLocaleDateString(dateLocale())
      : t("noDate"),
  },
  {
    heading: t("source"),
    text: datasheet.source ?? null,
  },
  {
    heading: t("fileType"),
    text:
      datasheet.file?.[0]?.ext
        ?.replace(/[^a-zA-Z0-9]/g, "")
        .toUpperCase() ?? null,
  },

  // 14
  {
    heading: t("citation"),
    text: datasheet.citation ?? null,
    colSize: 8,
  },

  // 15
  {
    heading: t("resources"),
    text: datasheet?.resources?.length ? JSON.stringify(datasheet.resources) : null,
    resources: datasheet?.resources?.length ? datasheet.resources : null,
  },
];
